import { Link as RRDLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';
import { mixinTextBase, mixinTextSmall, mixinTextXSmall } from '../../layout/mixins';
import { ButtonReset } from '../../layout/reset';

interface WrapperProps {
  $isRemoved?: boolean;
}

export const Wrapper = styled.div.withConfig({ componentId: 'sc-up2wg0-0' })<WrapperProps>`
    --_accent-color: var(--c-accent-bg);
    --_zindex-image-backdrop: 0;
    --_zindex-image: 1;
    --_zindex-save-button: 3;
    --_zindex-title-hitbox: 2;

    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${size(2)};
    height: 100%;
    user-select: none;
    isolation: isolate;

    ${({ $isRemoved }) =>
      $isRemoved &&
      css`
            --_seller-color: var(--c-text-muted);
            --_title-color: var(--c-text-muted);
            --_image-opacity: 0.37;
        `}
`;

export const Media = styled.div.withConfig({ componentId: 'sc-up2wg0-1' })`
    position: relative;
    aspect-ratio: 3 / 2;
    background-color: var(--c-bg-alt);
    overflow: hidden;
`;

export const Image = styled.img.withConfig({ componentId: 'sc-up2wg0-2' })`
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    z-index: var(--_zindex-image);
    opacity: var(--_image-opacity, 1);
`;

export const ImageBackdrop = styled(Image).withConfig({ componentId: 'sc-up2wg0-3' })`
    object-fit: cover;
    filter: blur(10px);
    z-index: var(--_zindex-image-backdrop);
`;

export const SaveButton = styled(ButtonReset).withConfig({ componentId: 'sc-up2wg0-4' })`
    --_size: ${size(4)};
    --_icon-size: ${size(2.25)};

    position: absolute;
    top: ${size(1)};
    right: ${size(1)};
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--_size);
    height: var(--_size);
    border-radius: 50%;
    color: var(--c-overlay-fg);
    font-size: var(--_icon-size);
    isolation: isolate;
    transform-origin: 50% 50%;
    transition: transform 200ms ease;
    user-select: none;
    cursor: pointer;
    z-index: var(--_zindex-save-button);

    &::before,
    &::after {
        content: "";
        position: absolute;
        border-radius: inherit;
        z-index: -1;
    }

    &::before {
        inset: -0;
        background-color: var(--c-overlay);
        opacity: 0.5;
        transition: opacity 200ms ease;
    }

    &::after {
        inset: -2px;
        border: 2px solid rgba(var(--c-overlay-inverted-rgb), 0.2);
    }

    &:not([disabled]):hover {
        &::before {
            opacity: 0.8;
        }
    }

    &:not(:active):hover {
        transform: scale(1.05);
    }

    &:active {
        transform: scale(1);
    }

    &[disabled] {
        opacity: 0.37;
        cursor: not-allowed;
    }
`;

export const Body = styled.div.withConfig({ componentId: 'sc-up2wg0-5' })`
    display: flex;
    flex-direction: column;
    gap: ${size(1)};
`;

export const Status = styled.div.withConfig({ componentId: 'sc-up2wg0-6' })`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${size(2)};
`;

export const Location = styled.div.withConfig({ componentId: 'sc-up2wg0-7' })`
    ${mixinTextXSmall};
    display: flex;
    align-items: center;
    gap: ${size(0.5)};
    color: var(--c-text-muted);
    font-weight: var(--font-weight-regular);
`;

interface StatusBadgeProps {
  $tone?: 'positive' | 'neutral' | 'negative';
  $size?: 'small' | 'large';
}

export const StatusBadge = styled.div.withConfig({ componentId: 'sc-up2wg0-8' })<StatusBadgeProps>`
    --_status-badge-padding: ${size(0.75)} ${size(1)};
    --_status-badge-gap: ${size(0.5)};
    --_status-badge-font-size: var(--font-size-small);
    --_status-badge-line-height: var(--line-height-small);

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--_status-badge-gap);
    padding: var(--_status-badge-padding);
    border-radius: ${size(0.5)};
    background-color: var(--_bg, var(--c-semantic-neutral-300));
    color: var(--_fg, var(--c-semantic-neutral-700));
    font-size: var(--_status-badge-font-size);
    line-height: var(--_status-badge-line-height);
    font-weight: var(--font-weight-medium);
    white-space: nowrap;

    svg {
        color: var(--_icon-color, var(--_fg));
    }

    ${({ $size }) =>
      $size === 'small'
        ? css`
                  --_status-badge-padding: ${size(0.5)} ${size(1)};
                  --_status-badge-gap: ${size(0.5)};
                  --_status-badge-font-size: var(--font-size-xsmall);
                  --_status-badge-line-height: var(--line-height-xsmall);
              `
        : $size === 'large'
          ? css`
                  --_status-badge-padding: ${size(0.75)} ${size(1.5)};
                  --_status-badge-gap: ${size(0.75)};
                  --_status-badge-font-size: var(--font-size-regular);
                  --_status-badge-line-height: var(--line-height-regular);
              `
          : undefined}

    ${({ $tone }) =>
      $tone === 'positive'
        ? css`
                  --_bg: var(--c-semantic-success-100);
                  --_fg: var(--c-semantic-success-700);
              `
        : $tone === 'neutral'
          ? css`
                  --_bg: var(--c-semantic-warning-100);
                  --_fg: var(--c-semantic-warning-700);
              `
          : $tone === 'negative'
            ? css`
                  --_bg: var(--c-semantic-error-100);
                  --_fg: var(--c-semantic-error-700);
              `
            : undefined}
`;

export const Seller = styled.div.withConfig({ componentId: 'sc-up2wg0-9' })`
    ${mixinTextXSmall};
    font-weight: var(--font-weight-medium);
    color: var(--_seller-color, var(--_accent-color));
`;

const titleStyles = css`
    ${mixinTextBase};
    font-weight: var(--font-weight-medium);
    color: var(--_title-color, var(--c-text-main));
    word-wrap: break-word;
    hyphens: auto;
`;

export const Title = styled(RRDLink).withConfig({ componentId: 'sc-up2wg0-10' })`
    ${titleStyles};

    &:hover {
        text-decoration: underline;
    }

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: var(--_zindex-title-hitbox);
    }
`;

export const StaticTitle = styled.div.withConfig({ componentId: 'sc-up2wg0-11' })`
    ${titleStyles};
`;

export const PriceLabelMain = styled.div.withConfig({ componentId: 'sc-up2wg0-12' })`
    ${mixinTextXSmall};
    font-weight: var(--font-weight-regular);
`;

export const PriceValueMain = styled.div.withConfig({ componentId: 'sc-up2wg0-13' })`
    color: var(--_accent-color);
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-h3);
    line-height: var(--line-height-h3);
`;

export const PriceLabelAlt = styled.div.withConfig({ componentId: 'sc-up2wg0-14' })`
    ${mixinTextSmall};
    font-weight: var(--font-weight-regular);
    color: var(--c-text-main);
`;

export const PriceValueAlt = styled.div.withConfig({ componentId: 'sc-up2wg0-15' })`
    ${mixinTextSmall};
    margin-left: ${size(0.75)};
    font-weight: var(--font-weight-regular);
    color: var(--_accent-color);
`;

export const Footer = styled.div.withConfig({ componentId: 'sc-up2wg0-16' })`
    display: flex;
    flex-direction: column;
    gap: ${size(0.5)};
    margin-top: auto;
`;

export const FooterMain = styled.div.withConfig({ componentId: 'sc-up2wg0-17' })`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${size(2)};
`;

export const FooterMainLogo = styled.img.withConfig({ componentId: 'sc-up2wg0-18' })`
    display: block;
    width: ${size(16)};
    max-width: 40%;
    height: ${size(4)};
    object-fit: contain;
    object-position: right;
`;

export const EcomBanner = styled.div.withConfig({ componentId: 'sc-up2wg0-19' })`
    display: flex;
    align-items: center;
    gap: ${size(0.5)};
    padding: ${size(1)} ${size(1.5)};
    margin-top: ${size(1.5)};
    background: var(--c-bg-alt);
    color: var(--c-text-main);
    font-size: var(--font-size-xsmall);
    font-weight: var(--font-weight-regular);
    border-radius: ${(props) => props.theme.size.borderRadius};

    svg {
        flex-shrink: 0;
    }
`;
